import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hexagon, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useBotGuard } from "@/lib/botGuard";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { withGuard, Honeypot } = useBotGuard();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [ttl, setTtl] = useState(15);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", withGuard({ email }));
      setTtl(data?.ttl_minutes || 15);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">
            Hive <span className="text-yellow-500">@</span> Buzzzillion
          </span>
        </Link>

        <div className="bg-white rounded-3xl border border-stone-200 p-8" data-testid="forgot-password-card">
          {!sent ? (
            <>
              <h1 className="font-display text-3xl font-medium">Forgot your password?</h1>
              <p className="text-stone-500 text-sm mt-1">
                Enter the email on your account and we'll send you a 6-digit reset code.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
                <Honeypot />
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? "Sending…" : "Send reset code"}
                </Button>
              </form>

              <p className="text-sm text-stone-500 mt-6 text-center">
                Remembered it?{" "}
                <Link to="/login" className="text-amber-700 font-medium hover:underline" data-testid="forgot-password-back-to-login">
                  Back to sign in
                </Link>
              </p>
            </>
          ) : (
            <div data-testid="forgot-password-sent">
              <div className="flex items-center gap-2 text-amber-700 mb-3">
                <MailCheck className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">Check your inbox</span>
              </div>
              <h1 className="font-display text-3xl font-medium">Code sent</h1>
              <p className="text-stone-500 text-sm mt-2 leading-relaxed">
                If an account exists for <strong className="text-stone-700">{email}</strong>, we've emailed a 6-digit reset code.
                It expires in <strong>{ttl} minutes</strong>. Check your spam folder if you don't see it.
              </p>

              <Button
                onClick={() => navigate(`/reset-password?email=${encodeURIComponent(email)}`)}
                className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 mt-6"
                data-testid="forgot-password-have-code-button"
              >
                I have my code
              </Button>

              <button
                type="button"
                onClick={() => { setSent(false); }}
                className="mt-4 w-full text-xs text-stone-500 hover:text-stone-800"
                data-testid="forgot-password-try-different-email"
              >
                ← Use a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
